import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Skeleton } from "../skeleton";
import { Button } from "../button";
import type { DataTableProps } from "./types";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from "./types";

export function DataTable<TData>({
  columns,
  data,
  totalRows,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  pagination,
  onPaginationChange,
  columnVisibility,
  onColumnVisibilityChange,
  rowSelection,
  onRowSelectionChange,
  isLoading = false,
  emptyMessage = "No results found",
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  enableRowSelection = false,
}: DataTableProps<TData>) {
  const isServerSide = !!onPaginationChange;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      pagination: pagination ?? { pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE },
      columnVisibility,
      rowSelection: rowSelection ?? {},
    },
    onSortingChange,
    onColumnFiltersChange,
    onPaginationChange,
    onColumnVisibilityChange,
    onRowSelectionChange,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isServerSide ? undefined : getSortedRowModel(),
    getFilteredRowModel: isServerSide ? undefined : getFilteredRowModel(),
    getPaginationRowModel: isServerSide ? undefined : getPaginationRowModel(),
    manualSorting: isServerSide,
    manualFiltering: isServerSide,
    manualPagination: isServerSide,
    rowCount: totalRows,
  });

  const pageCount = totalRows
    ? Math.ceil(totalRows / (pagination?.pageSize ?? DEFAULT_PAGE_SIZE))
    : table.getPageCount();
  const currentPage = (pagination?.pageIndex ?? 0) + 1;

  const selectedCount = Object.keys(rowSelection ?? {}).length;

  return (
    <div className="w-full space-y-4">
      {selectedCount > 0 && (
        <div
          className="bg-muted rounded-badge flex items-center gap-2 px-4 py-2 text-[13px]"
          role="status"
        >
          {selectedCount} row{selectedCount !== 1 ? "s" : ""} selected
        </div>
      )}

      <div className="rounded-card overflow-x-auto border">
        <table className="w-full text-[13px]" role="table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-muted/50 border-b">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className="text-muted-foreground px-4 py-3 text-left text-[10.5px] font-bold tracking-[0.04em] uppercase"
                      aria-sort={
                        sortDir === "asc"
                          ? "ascending"
                          : sortDir === "desc"
                            ? "descending"
                            : undefined
                      }
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          className="hover:text-foreground focus-visible:ring-ring/50 flex items-center gap-1 rounded-sm outline-none transition-colors duration-150 focus-visible:ring-[3px] motion-reduce:transition-none"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <span
                            className="inline-block w-4 text-xs"
                            aria-hidden="true"
                          >
                            {sortDir === "asc"
                              ? "\u25B2"
                              : sortDir === "desc"
                                ? "\u25BC"
                                : "\u25C6"}
                          </span>
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: pagination?.pageSize ?? 5 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-b">
                  {columns.map((_, j) => (
                    <td key={`skeleton-${i}-${j}`} className="px-4 py-3">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-muted-foreground px-4 py-12 text-center"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-muted/50 data-[selected]:bg-primary/5 data-[selected]:hover:bg-primary/10 border-b transition-colors duration-150 motion-reduce:transition-none"
                  data-selected={row.getIsSelected() || undefined}
                  aria-selected={row.getIsSelected() || undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    // B2: table-wide tabular-nums keeps numeric columns aligned
                    // with zero consumer edits (harmless on text — only digit
                    // glyphs are affected).
                    <td key={cell.id} className="px-4 py-3 tabular-nums">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(onPaginationChange || !isServerSide) && pageCount > 1 && (
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="text-muted-foreground flex items-center gap-2 text-[13px]">
            <label htmlFor="page-size-select">Rows per page:</label>
            <select
              id="page-size-select"
              value={pagination?.pageSize ?? DEFAULT_PAGE_SIZE}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                table.setPageSize(newSize);
              }}
              className="border-input rounded-field h-8 border bg-transparent px-2 text-[13px] tabular-nums"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-[13px] tabular-nums">
              Page {currentPage} of {pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="rounded-badge"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-badge"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
